import { useEffect, useRef, useState } from "react";
import { Radio } from "lucide-react";

/**
 * The reservation lifecycle the board cycles a ride through, in order. A row
 * advances one step each tick; once it reaches the terminal `COMPLETED` state it
 * rolls off the top and a fresh upcoming ride is appended at the bottom.
 */
const STATUS_FLOW = ["PENDING", "CONFIRMED", "IN PROGRESS", "COMPLETED"] as const;
type Status = (typeof STATUS_FLOW)[number];

/** Tone bucket per status, mapped to a design-system color token below. */
type Tone = "pending" | "confirmed" | "live" | "completed";

const STATUS_TONE: Record<Status, Tone> = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  "IN PROGRESS": "live",
  COMPLETED: "completed",
};

/** Status label color tokens — flip-safe across light/dark. */
const TONE_TEXT: Record<Tone, string> = {
  live: "text-live-500",
  confirmed: "text-success-500",
  pending: "text-warning-500",
  completed: "text-neutral-400",
};

/** A single decorative departure-board row. */
interface BoardRow {
  /** Stable identity so React keeps DOM nodes across status mutations. */
  id: number;
  /** Scheduled pickup time, "HH:MM" (24h), font-mono tabular-nums. */
  time: string;
  /** Illustrative reservation reference, e.g. "RR-4821". */
  ref: string;
  /** Illustrative route, e.g. "JFK → MIDTOWN". */
  route: string;
  /** Current lifecycle status. */
  status: Status;
}

/**
 * Illustrative airport/neighborhood endpoints used to synthesize new upcoming
 * rides. Purely decorative copy — not real data, no fabricated aggregate stats.
 */
const ENDPOINTS = [
  "JFK",
  "LGA",
  "EWR",
  "MIDTOWN",
  "SOHO",
  "TRIBECA",
  "BROOKLYN",
  "HARLEM",
  "CHELSEA",
  "QUEENS",
] as const;

/** Initial board — a believable spread across the lifecycle. */
const INITIAL_ROWS: readonly BoardRow[] = [
  { id: 1, time: "09:15", ref: "RR-4821", route: "JFK → MIDTOWN", status: "IN PROGRESS" },
  { id: 2, time: "09:40", ref: "RR-4822", route: "BROOKLYN → LGA", status: "CONFIRMED" },
  { id: 3, time: "10:05", ref: "RR-4823", route: "NEWARK → SOHO", status: "PENDING" },
  { id: 4, time: "10:30", ref: "RR-4824", route: "MIDTOWN → JFK", status: "CONFIRMED" },
  { id: 5, time: "10:55", ref: "RR-4825", route: "LGA → TRIBECA", status: "PENDING" },
];

/** Tick cadence for the live state machine, in milliseconds. */
const TICK_MS = 2500;

/** Spacing between synthesized pickup times, in minutes. */
const SLOT_MINUTES = 25;

/**
 * Add `minutes` to an "HH:MM" string and return the new "HH:MM" (24h, wrapping
 * past midnight). Keeps the board's times marching forward without dates.
 * @param time - The base time, "HH:MM".
 * @param minutes - Minutes to add.
 * @returns The advanced "HH:MM" string, zero-padded.
 */
function addMinutes(time: string, minutes: number): string {
  const [h = 0, m = 0] = time.split(":").map(Number);
  const total = (h * 60 + m + minutes) % (24 * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/**
 * Advance a status one step along the lifecycle; `COMPLETED` is terminal.
 * @param status - The current status.
 * @returns The next status, or the same status if already terminal.
 */
function nextStatus(status: Status): Status {
  const i = STATUS_FLOW.indexOf(status);
  return STATUS_FLOW[i + 1] ?? status;
}

/** The next board state for one tick, plus which row visibly changed. */
interface Tick {
  /** The next rows (always the same length as the input). */
  rows: BoardRow[];
  /** Id of the row whose status changed this tick (to flip its tile). */
  changedId: number;
}

/**
 * Compute the next board state for one tick:
 *  - if the top row is COMPLETED, roll it off and append a fresh upcoming ride
 *    (the new row is the one that "changed");
 *  - otherwise advance the earliest not-yet-complete row one lifecycle step.
 * Pure (input → new state) so it's trivially unit-testable, and it reports the
 * changed row's id directly rather than diffing the result by index — which
 * would mis-identify the row on a roll-off (every index shifts up).
 * @param rows - The current rows.
 * @param seq - Monotonic counter used to mint a stable id + ref for new rows.
 * @returns The next rows and the changed row's id.
 */
function advanceBoard(rows: readonly BoardRow[], seq: number): Tick {
  // Roll a finished ride off the top and bring a new one on at the bottom.
  const last = rows[rows.length - 1];
  if (rows[0]?.status === "COMPLETED" && last) {
    const from = ENDPOINTS[seq % ENDPOINTS.length] ?? "MIDTOWN";
    const to = ENDPOINTS[(seq * 3 + 1) % ENDPOINTS.length] ?? "JFK";
    const route = from === to ? `${from} → MIDTOWN` : `${from} → ${to}`;
    const newRow: BoardRow = {
      id: seq,
      time: addMinutes(last.time, SLOT_MINUTES),
      ref: `RR-${String(4820 + (seq % 180))}`,
      route,
      status: "PENDING",
    };
    return { rows: [...rows.slice(1), newRow], changedId: newRow.id };
  }

  // Otherwise advance the first row that still has somewhere to go.
  const target = rows.find((r) => r.status !== "COMPLETED");
  if (!target) return { rows: [...rows], changedId: -1 };
  return {
    rows: rows.map((r) =>
      r.id === target.id ? { ...r, status: nextStatus(r.status) } : r
    ),
    changedId: target.id,
  };
}

/**
 * True when the user has asked the OS to minimize non-essential motion.
 * @returns Whether `prefers-reduced-motion: reduce` currently matches.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Decorative, live "split-flap" departure board for the marketing hero.
 *
 * Unlike the old CSS-only mock, this board is genuinely state-changing: a React
 * `setInterval` advances rides along the reservation lifecycle
 * (PENDING → CONFIRMED → IN PROGRESS → COMPLETED), rolls completed rides off the
 * top, brings new upcoming rides on at the bottom, and ticks times forward — an
 * honest "live dispatch console" texture that sells the product feel.
 *
 * Rendered as a softened `bg-surface` panel with a hairline border (not a
 * jarring ink block) so it integrates into the page and is flip-safe in both
 * light and dark themes. Geist Mono columns; status tones via design tokens.
 *
 * Accessibility: the whole board is `aria-hidden` (purely decorative), and it
 * respects `prefers-reduced-motion` — under reduced motion no interval is
 * scheduled and the board renders static (the pulse is `motion-safe:` only).
 *
 * Contract: named export, no required props.
 * @returns The departure board element.
 */
export function DepartureBoard() {
  // Rows plus flip bookkeeping in one state object so the per-tick update stays
  // a single pure reducer. `flip.id` is the row that last changed and
  // `flip.token` is a monotonic counter feeding that row's status `key`, so the
  // CSS flip restarts even when the same row advances on back-to-back ticks
  // (an identical className alone would not re-fire the animation).
  const [board, setBoard] = useState<{
    rows: readonly BoardRow[];
    flip: { id: number; token: number };
  }>({ rows: INITIAL_ROWS, flip: { id: -1, token: 0 } });
  const { rows, flip } = board;

  // Monotonic counter for minting stable ids/refs on rolled-in rows. A ref (not
  // state) so it never triggers a render on its own.
  const seqRef = useRef<number>(INITIAL_ROWS.length + 1);

  useEffect(() => {
    // Static board under reduced motion — no interval is scheduled at all.
    if (prefersReducedMotion()) return;

    const id = window.setInterval(() => {
      setBoard((current) => {
        const tick = advanceBoard(current.rows, seqRef.current++);
        return {
          rows: tick.rows,
          flip: { id: tick.changedId, token: current.flip.token + 1 },
        };
      });
    }, TICK_MS);

    return () => {
      window.clearInterval(id);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className="overflow-hidden rounded-md border border-neutral-200 bg-surface"
    >
      {/* Status-line header. `bg-neutral-100` (a compiled, flip-safe utility) —
          NOT `bg-background-subtle`, which has no `--color-background-subtle`
          mapping in the @theme bridge and so silently renders transparent. */}
      <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-100 px-4 py-3">
        <div className="flex items-center gap-2 text-neutral-600">
          <Radio className="h-3.5 w-3.5 text-live-500" strokeWidth={1.75} />
          <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em]">
            Departures
          </span>
          <span className="text-neutral-300">·</span>
          <span className="font-mono text-xs uppercase tabular-nums tracking-wider text-neutral-500">
            Today
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-live-600">
          <span className="size-1.5 rounded-full bg-live-500 motion-safe:animate-pulse" />
          Live
        </span>
      </div>

      {/* Column legend */}
      <div className="grid grid-cols-[3.25rem_4.5rem_1fr_auto] items-center gap-x-3 border-b border-neutral-200 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 sm:gap-x-4">
        <span>Time</span>
        <span>Ride</span>
        <span>Route</span>
        <span className="text-right">Status</span>
      </div>

      {/* Live rows */}
      <ul className="divide-y divide-neutral-200">
        {rows.map((row) => {
          const tone = STATUS_TONE[row.status];
          const isLive = tone === "live";
          return (
            <li
              key={row.id}
              className={`relative grid grid-cols-[3.25rem_4.5rem_1fr_auto] items-center gap-x-3 px-4 py-3 font-mono text-sm tabular-nums sm:gap-x-4 ${
                isLive ? "bg-live-50/60" : ""
              }`}
            >
              {/* Left accent rail marks the in-progress ride. */}
              {isLive && (
                <span className="absolute inset-y-0 left-0 w-0.5 bg-live-500" />
              )}
              <span className="font-semibold text-neutral-700">{row.time}</span>
              <span className="text-neutral-400">{row.ref}</span>
              <span className="truncate tracking-wide text-neutral-700">
                {row.route}
              </span>
              <span
                // Re-key the span on each flip of THIS row so React remounts it
                // and the CSS animation restarts — even on back-to-back flips.
                key={flip.id === row.id ? flip.token : "static"}
                className={`text-right text-xs font-semibold tracking-widest ${TONE_TEXT[tone]} ${
                  flip.id === row.id
                    ? "motion-safe:animate-[departure-flip_420ms_ease-out]"
                    : ""
                }`}
                style={{ transformOrigin: "top" }}
              >
                {row.status}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Local keyframes for the flip micro-animation. Scoped to this component
          so we never touch globals.css; honored only under motion-safe. */}
      <style>{`
        @keyframes departure-flip {
          0% { transform: rotateX(-82deg); opacity: 0.35; }
          60% { transform: rotateX(8deg); opacity: 1; }
          100% { transform: rotateX(0deg); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
